import { blinkConstants } from '@thoughtspot/blink-constants';
import { isCommandPaletteEnabled } from '@thoughtspot/session-service';
import React, { useEffect } from 'react';
import { useAppContext } from '/@contexts/global-app-context';
import { isEmbeddedApp } from '/@utils/embed-base.util';
import { EurekaMainSearchBar } from '../../microfrontends/eureka';
import styles from './nav-search-bar.module.scss';

interface NavSearchBarContainerProps {
    eurekaSearchBarOnFocus?: () => void;
    isEurekaPath?: boolean;
}

export const NavSearchBarContainer: React.FC<NavSearchBarContainerProps> = ({
    eurekaSearchBarOnFocus,
    isEurekaPath,
}) => {
    const { navService, sessionService } = useAppContext();

    const shouldFocusOnInit = () => {
        const shouldFocus =
            localStorage.getItem(blinkConstants.eureka.focusOnInit) ===
            true.toString();
        localStorage.setItem(
            blinkConstants.eureka.focusOnInit,
            false.toString(),
        );
        return shouldFocus;
    };

    // Only intercept the search bar click for Spotlight in non-embedded (TSA)
    // mode. In TSE, isEmbeddedApp() is true and Spotlight is not rendered, so
    // the interception must be skipped to allow the normal Eureka search modal
    // to open.
    const commandPaletteEnabled = isCommandPaletteEnabled() && !isEmbeddedApp();

    const openSpotlight = () => {
        (document.activeElement as HTMLElement)?.blur();
        window.dispatchEvent(new CustomEvent('spotlight:open'));
    };

    // When spotlight closes, return focus to the help item
    useEffect(() => {
        if (!commandPaletteEnabled) return undefined;
        const handleClose = () => {
            // The radiant Button puts id on a wrapper div — query the inner <button>
            const button = document.querySelector<HTMLElement>(
                '#help-menu-button button',
            );
            button?.focus();
        };
        window.addEventListener('spotlight:close', handleClose);
        return () => window.removeEventListener('spotlight:close', handleClose);
    }, [commandPaletteEnabled]);

    const handleMouseDown = commandPaletteEnabled
        ? (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              openSpotlight();
          }
        : undefined;

    const handleFocus = commandPaletteEnabled
        ? (e: React.FocusEvent) => {
              e.stopPropagation();
              (e.target as HTMLElement).blur();
              openSpotlight();
          }
        : undefined;

    const isMac = navigator.platform.toUpperCase().includes('MAC');

    return (
        <div
            className={`${styles.searchBarWrapper}${
                commandPaletteEnabled ? ` ${styles.commandPaletteMode}` : ''
            }`}
            onMouseDownCapture={handleMouseDown}
            onFocusCapture={handleFocus}
        >
            <EurekaMainSearchBar
                getChooseDataSourcesComponent={null}
                focusOnInit={shouldFocusOnInit()}
                sessionService={sessionService}
                onFocusChanged={eurekaSearchBarOnFocus}
                isNavbarMode
                isGlobalNavEnabled
                isHomepage={false}
                goToEurekaWithQueryParams={navService.goToEurekaWithQueryParams}
                isEurekaPath={isEurekaPath}
            />
            {commandPaletteEnabled && (
                <span className={styles.cmdKBadge}>
                    <span className={styles.cmdKey}>
                        {isMac ? '⌘' : 'Ctrl'}
                    </span>
                    <span className={styles.cmdKey}>K</span>
                </span>
            )}
        </div>
    );
};
